import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory, { MaskableFeeSchedule } from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/masked-fields";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { validate, WorkAgreementJSON, WorkAgreementParams } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "./teams/client-team/client-team";
import TeamLeaderHandover, { WorkTeamSide } from "./teams/leader-handover/team-leader-handover";
import VendorTeamDeparture from "./teams/vendor-team/vendor-team-departure";
import VendorTeam from "./teams/vendor-team/vendor-team";

export type MaskableTeam = VendorTeam | MaskedTeam | undefined;

/**
 * Work Agreements are ultimately the end product of a successful negotiation
 * and represent the basis for all of the proposal fields
 */
export default class WorkAgreement {
  public static get EXAMPLE(): WorkAgreement {
    return new WorkAgreement({
      id: Guid.generate(),
      RFPId: Guid.generate(),
      name: ProjectName.create("Test Agreement"),
      description: ProjectDescription.create("Description"),
      additionalVendors: [EntityMember.EXAMPLE],
      discount: new Percent(0.3),
      startDate: new AHDate(),
      endDate: new AHDate().add(1, "month"),
      hireDate: undefined,
      teamRestricted: new AHBoolean(false),
      conflicts: [WorkDocument.EXAMPLE],
      vendorFeeScheduleTemplateIds: [Guid.generate()],
      vendorPolicies: [WorkDocument.EXAMPLE],
      vendorTeamTemplateIds: [Guid.generate()],
      clientPolicies: [WorkDocument.EXAMPLE],
      clientFeeScheduleTemplateIds: [Guid.generate()],
      clientTeamTemplateIds: [Guid.generate()],
      archiveDate: undefined,
      conflictsCheckWaived: new AHBoolean(false),
      clientTeam: ClientTeam.EXAMPLE,
      vendorTeam: VendorTeam.EXAMPLE,
      feeSchedule: [FeeScheduleCategory.EXAMPLE],
    });
  }

  public static fromJSON(json: WorkAgreementJSON): WorkAgreement {
    const parsed = new WorkAgreementSchema().deserialize(json);
    return new WorkAgreement({ ...parsed });
  }

  public static create(params: WorkAgreementParams): WorkAgreement {
    const parsed = validate(params);
    return new WorkAgreement(parsed);
  }

  public readonly id?: Guid;
  public readonly version?: number;
  /**
   * Which version of the work this agreement is, from 1, so agreements that share a project name can
   * be told apart (#6636). Undefined until it is hired. Not `version`, which is for concurrency.
   */
  public readonly versionNumber?: number;
  /**
   * The version this agreement becomes, or became, when it is hired (#6636). Undefined on a payload
   * older than the field.
   */
  public readonly proposedVersionNumber?: number;
  public readonly hireDate?: AHDate;
  public readonly archiveDate?: AHDate;
  /**
   * When the reader left this work, when what they are reading is the view they kept of it: the
   * agreement as it was at that moment, and nothing after (#5833). Undefined on a live read.
   */
  public readonly frozenAt?: AHDate;
  public readonly params: WorkAgreementParams;
  public readonly RFPId?: Guid;
  public readonly clientTeam?: ClientTeam | MaskedTeam;
  public readonly name?: ProjectName;
  public readonly description?: ProjectDescription;
  public readonly startDate?: AHDate | null;
  public readonly endDate?: AHDate | null;
  public readonly vendorTeam?: VendorTeam | MaskedTeam;
  public readonly teamRestricted: AHBoolean | MaskedTeam = new AHBoolean(false);
  public readonly conflictsCheckWaived: AHBoolean | MaskedContracts = new AHBoolean(false);
  public readonly discount?: Percent | MaskedDiscount;
  /**
   * The active agreement this one was opened to revise, when it began life as a revision. Set when
   * the revision is drafted and never changes, so it is present on the revision while it is still
   * being negotiated as well as after it is hired. Undefined for a first-time agreement.
   */
  public readonly replacesWorkAgreementId?: Guid;
  /**
   * The revision that was hired in this agreement's place -- the moment that hire ended this one.
   * Undefined while the agreement is active, and for an agreement that ended any other way.
   */
  public readonly replacedByWorkAgreementId?: Guid;
  /**
   * A revision opened against this agreement that has not been hired yet, when the reader is allowed
   * to know about it. Undefined when there is none -- and undefined for the counterparty while the
   * revision is still an unsubmitted draft, because opening one is private to the party that did.
   */
  public readonly openRevisionProposalId?: Guid;
  /**
   * The agreement whose forums hold this agreement's conversation -- the first one in its revision
   * chain. Undefined only for an agreement read from an older payload, where this agreement's own id
   * is the answer; use `conversationId`.
   */
  public readonly conversationWorkAgreementId?: Guid;
  /**
   * The proposal that was hired to create this agreement -- the negotiation whose acceptance
   * produced it, whether this is a first-time agreement or a revision. Undefined only when that
   * proposal cannot be read, a data integrity gap rather than an ordinary outcome.
   */
  public readonly sourceProposalId?: Guid;

  public get additionalVendors(): EntityMember[] {
    return [...this._additionalVendors];
  }
  /**
   * Everyone who has been on the vendor team and is not on it now, oldest departure first, each one
   * saying why they left: they took themselves off, or somebody else did (#5663).
   *
   * Empty for a reader the vendor team is masked from, and empty on an agreement nobody has left.
   */
  public get vendorTeamDepartures(): VendorTeamDeparture[] {
    return [...this._vendorTeamDepartures];
  }

  /**
   * The handovers of a team's leadership waiting for an answer that this reader may know about: at
   * most one for each team (#5836).
   */
  public get pendingLeaderHandovers(): TeamLeaderHandover[] {
    return [...this._pendingLeaderHandovers];
  }


  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    return Masked.isMasked(this._feeSchedule) ? this._feeSchedule : [...this._feeSchedule];
  }

  public get clientPolicies(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._clientPolicies)
      ? this._clientPolicies
      : this._clientPolicies.map((doc) => doc.clone());
  }

  public get vendorPolicies(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._vendorPolicies)
      ? this._vendorPolicies
      : this._vendorPolicies.map((doc) => doc.clone());
  }

  public get conflicts(): WorkDocument[] | MaskedContracts {
    return Masked.isMasked(this._conflicts) ? this._conflicts : this._conflicts.map((doc) => doc.clone());
  }
  public get clientTeamTemplateIds(): Guid[] {
    return [...this._clientTeamTemplateIds];
  }
  public get vendorTeamTemplateIds(): Guid[] {
    return [...this._vendorTeamTemplateIds];
  }
  public get clientFeeScheduleTemplateIds(): Guid[] {
    return [...this._clientFeeScheduleTemplateIds];
  }
  public get vendorFeeScheduleTemplateIds(): Guid[] {
    return [...this._vendorFeeScheduleTemplateIds];
  }

  public get associatedUserIds(): Guid[] {
    const userIds: Guid[] = [];
    if (!Masked.isMasked(this.clientTeam)) {
      if (this.clientTeam?.leader?.userId) userIds.push(this.clientTeam.leader.userId);
      this.clientTeam?.members.forEach((member) => {
        if (member.userId) userIds.push(member.userId);
      });
    }
    if (!Masked.isMasked(this.vendorTeam)) {
      if (this.vendorTeam?.leader?.userId) userIds.push(this.vendorTeam.leader.userId);
      this.vendorTeam?.members.forEach((member) => {
        if (member.userId) userIds.push(member.userId);
      });
    }
    return userIds;
  }

  public get vendor(): EntityMember | MaskedTeam | undefined {
    if (Masked.isMasked(this.vendorTeam)) {
      return this.vendorTeam;
    }
    return this.vendorTeam?.leader;
  }

  public get isArchived(): boolean {
    return this.archiveDate !== undefined;
  }
  /** Whether this is the view a reader kept of work they left, rather than the work as it is. */
  public get isFrozenForReader(): boolean {
    return this.frozenAt !== undefined;
  }
  public get isHired(): boolean {
    return this.hireDate !== undefined;
  }
  /**
   * True while this is hired work that is still running: the client's reading of the domain's
   * `WorkAgreement.IsActiveAt` — hired, not archived, and not already past its end date.
   */
  public get isActiveWork(): boolean {
    if (!this.isHired || this.isArchived) return false;
    return !this.endDate?.isSameOrBefore(new AHDate());
  }
  /**
   * True while who is on the vendor team is itself a term of the agreement: the mirror of the
   * domain's `VendorTeamRosterIsAnAgreedTerm`, which is the predicate
   * `PUT work/{id}/vendor-team-members` turns on. A restricted roster on live work moves only
   * through a revision both parties negotiate; anything else is the vendor's own staffing and
   * changing it re-negotiates nothing.
   *
   * A reader who may not see whether the team is restricted is told that it is a term. The server
   * decides either way, and a UI that guessed the other way would offer a change it cannot know
   * will be taken in place.
   */
  public get vendorTeamRosterIsAnAgreedTerm(): boolean {
    if (Masked.isMasked(this.teamRestricted)) return true;
    return this.teamRestricted.value === true && this.isActiveWork;
  }
  /** True once a revision of this agreement has been hired, ending it. */
  public get isReplaced(): boolean {
    return this.replacedByWorkAgreementId !== undefined;
  }
  /** True when this agreement was opened as a revision of another. */
  public get revisesWorkAgreement(): boolean {
    return this.replacesWorkAgreementId !== undefined;
  }
  /** True when a revision is in flight against this agreement and the reader may know about it. */
  public get hasOpenRevisionRequest(): boolean {
    return this.openRevisionProposalId !== undefined;
  }
  /** True when the proposal that was hired to create this agreement can be read. */
  public get hasSourceProposal(): boolean {
    return this.sourceProposalId !== undefined;
  }
  /**
   * Where this agreement's chat lives. A revision continues the conversation of the agreement it
   * replaced instead of opening an empty one, so the two file their forums under one id.
   */
  public get conversationId(): Guid | undefined {
    return this.conversationWorkAgreementId ?? this.id;
  }

  public constructor(params: WorkAgreementParams) {
    this.params = params;
    this.id = params.id;
    this.version = params.version;
    this.versionNumber = params.versionNumber;
    this.proposedVersionNumber = params.proposedVersionNumber;
    this.hireDate = params.hireDate;
    this.archiveDate = params.archiveDate;
    this.frozenAt = params.frozenAt;
    this.RFPId = params.RFPId;
    this.clientTeam = params.clientTeam;
    this.name = params.name;
    this.description = params.description;
    this.startDate = params.startDate;
    this.endDate = params.endDate;
    this.vendorTeam = params.vendorTeam;
    this.teamRestricted = params.teamRestricted ?? new AHBoolean(false);
    this._additionalVendors = params.additionalVendors ? [...params.additionalVendors] : [];
    this._vendorTeamDepartures = params.vendorTeamDepartures ? [...params.vendorTeamDepartures] : [];
    this._pendingLeaderHandovers = params.pendingLeaderHandovers ? [...params.pendingLeaderHandovers] : [];
    this._feeSchedule = params.feeSchedule ?? [];
    this._clientPolicies = params.clientPolicies ?? [];
    this._vendorPolicies = params.vendorPolicies ?? [];
    this.conflictsCheckWaived = params.conflictsCheckWaived ?? new AHBoolean(false);
    this._conflicts = params.conflicts ?? [];
    this.discount = params.discount;
    this.replacesWorkAgreementId = params.replacesWorkAgreementId;
    this.replacedByWorkAgreementId = params.replacedByWorkAgreementId;
    this.openRevisionProposalId = params.openRevisionProposalId;
    this.conversationWorkAgreementId = params.conversationWorkAgreementId;
    this.sourceProposalId = params.sourceProposalId;
    this._clientTeamTemplateIds = params.clientTeamTemplateIds ? [...params.clientTeamTemplateIds] : [];
    this._vendorTeamTemplateIds = params.vendorTeamTemplateIds ? [...params.vendorTeamTemplateIds] : [];
    this._clientFeeScheduleTemplateIds = params.clientFeeScheduleTemplateIds
      ? [...params.clientFeeScheduleTemplateIds]
      : [];
    this._vendorFeeScheduleTemplateIds = params.vendorFeeScheduleTemplateIds
      ? [...params.vendorFeeScheduleTemplateIds]
      : [];
  }

  /** The pending handover of `side`'s team, if this reader may know about one. */
  public pendingLeaderHandoverOf(side: WorkTeamSide): TeamLeaderHandover | undefined {
    return this._pendingLeaderHandovers.find((handover) => handover.side === side);
  }

  /** The departure of `userId`, or undefined when that person has not left this vendor team. */
  public departureOf(userId: Guid | undefined): VendorTeamDeparture | undefined {
    if (!userId) return undefined;
    return this._vendorTeamDepartures.find((departure) => departure.userId.isEqualTo(userId));
  }

  public isAssociatedWithUserId(userId: Guid | undefined): boolean {
    if (!userId) return false;

    if (!Masked.isMasked(this.clientTeam) && this.clientTeam?.leader?.userId.isEqualTo(userId)) {
      return true;
    }

    if (
      !Masked.isMasked(this.vendorTeam) &&
      (this.vendorTeam?.leader?.userId.isEqualTo(userId) ||
        this.vendorTeam?.members.some((member) => member.userId.isEqualTo(userId)))
    ) {
      return true;
    }

    return false;
  }

  public clone(overrides: Partial<WorkAgreementParams> = {}): WorkAgreement {
    const clonedWorkAgreement = new WorkAgreement({
      id: this.id?.clone(),
      version: this.version,
      versionNumber: this.versionNumber,
      proposedVersionNumber: this.proposedVersionNumber,
      hireDate: this.hireDate?.clone(),
      archiveDate: this.archiveDate?.clone(),
      frozenAt: this.frozenAt?.clone(),
      additionalVendors: this._additionalVendors.map((vendor) => vendor.clone()),
      clientFeeScheduleTemplateIds: this._clientFeeScheduleTemplateIds.map((id) => id.clone()),
      clientTeamTemplateIds: this.clientTeamTemplateIds.map((id) => id.clone()),
      conflictsCheckWaived: this.conflictsCheckWaived.clone(),
      conflicts: this.conflicts,
      description: this.description ? this.description.clone() : undefined,
      discount: this.discount?.clone(),
      endDate: this.endDate === null ? null : this.endDate ? this.endDate.clone() : undefined,
      feeSchedule: this.feeSchedule,
      name: this.name?.clone(),
      RFPId: this.RFPId?.clone(),
      startDate: this.startDate === null ? null : this.startDate ? this.startDate.clone() : undefined,
      teamRestricted: this.teamRestricted.clone(),
      vendorTeam: this.vendorTeam?.clone(),
      vendorTeamDepartures: this._vendorTeamDepartures.map((departure) => departure.clone()),
      pendingLeaderHandovers: this._pendingLeaderHandovers.map((handover) => handover.clone()),
      clientTeam: this.clientTeam?.clone(),
      vendorFeeScheduleTemplateIds: this._vendorFeeScheduleTemplateIds.map((id) => id.clone()),
      clientPolicies: this.clientPolicies,
      vendorPolicies: this.vendorPolicies,
      vendorTeamTemplateIds: this.vendorTeamTemplateIds.map((id) => id.clone()),
      replacesWorkAgreementId: this.replacesWorkAgreementId?.clone(),
      replacedByWorkAgreementId: this.replacedByWorkAgreementId?.clone(),
      openRevisionProposalId: this.openRevisionProposalId?.clone(),
      conversationWorkAgreementId: this.conversationWorkAgreementId?.clone(),
      sourceProposalId: this.sourceProposalId?.clone(),
      ...overrides,
    });

    return clonedWorkAgreement;
  }

  public toJSON(): WorkAgreementJSON {
    return new WorkAgreementSchema().serialize(this.params);
  }

  private readonly _additionalVendors: EntityMember[] = [];
  private readonly _vendorTeamDepartures: VendorTeamDeparture[] = [];
  private readonly _pendingLeaderHandovers: TeamLeaderHandover[] = [];
  private readonly _feeSchedule: MaskableFeeSchedule = [];

  private readonly _clientPolicies: WorkDocument[] | MaskedContracts = [];
  private readonly _vendorPolicies: WorkDocument[] | MaskedContracts = [];
  private readonly _conflicts: WorkDocument[] | MaskedContracts = [];

  private readonly _clientTeamTemplateIds: Guid[] = [];
  private readonly _vendorTeamTemplateIds: Guid[] = [];
  private readonly _clientFeeScheduleTemplateIds: Guid[] = [];
  private readonly _vendorFeeScheduleTemplateIds: Guid[] = [];
}

export enum WorkAgreementFieldCategory {
  Details = "Details",
  Team = "Team",
  FeeSchedule = "Fee Schedule",
  Conflicts = "Conflicts",
  Policies = "Policies",
  Discount = "Discount",
  Invoices = "Invoices",
}
